// src/contexts/AuthContext.js
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut, onAuthStateChanged,
  updatePassword, reauthenticateWithCredential,
  EmailAuthProvider, GoogleAuthProvider,
  signInWithPopup, signInWithRedirect,
} from "firebase/auth";
import { terminate, clearIndexedDbPersistence, waitForPendingWrites } from "firebase/firestore";
import { auth, db } from "../services/firebase";
import { getProfil, creerProfil, creerOrganisation, getOrgsDeUser, updatePseudo } from "../services/db";
import { envoyerEmailInscription, envoyerLienMotDePasse } from "../services/brevo";

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [profil, setProfil] = useState(null);
  const [orgs, setOrgs] = useState([]); // toutes les orgs de l'user
  const [activeOrgId, setActiveOrgId] = useState(null);
  const [activeRole, setActiveRole] = useState(null);
  const [loading, setLoading] = useState(true);
  // Compte connecté sans association (1re connexion Google, inscription interrompue)
  // → écran « Nom de votre association »
  const [besoinAsso, setBesoinAsso] = useState(false);
  const inscriptionEnCours = useRef(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        // Charge le profil puis les organisations réellement accessibles
        async function charger() {
          let prof = null;
          try { prof = await getProfil(user.uid); } catch { prof = null; }
          let liste = [];
          if (prof?.orgIds?.length > 0) {
            try {
              await user.getIdToken(true);
              liste = await getOrgsDeUser(user.uid);
            } catch (e) {
              console.warn("Chargement des organisations impossible :", e?.code || e?.message);
              liste = [];
            }
          }
          return { prof, liste };
        }

        // Lu AVANT tout await : l'inscription peut se terminer pendant le chargement
        const enInscription = inscriptionEnCours.current;
        const { prof: p, liste: userOrgs } = await charger();

        // Compte sans aucune association accessible (1re connexion Google, inscription
        // interrompue, bénévole retiré de toutes ses associations) : on affiche l'écran
        // « Nom de votre association » au lieu d'en inventer une.
        // Jamais pendant une inscription en cours ni sur une page d'invitation.
        const surInvitation = window.location.pathname.startsWith("/rejoindre");
        setBesoinAsso(userOrgs.length === 0 && !enInscription && !inscriptionEnCours.current && !surInvitation);
        setProfil(p);
        setOrgs(userOrgs);
        if (userOrgs.length === 1) {
          // Une seule org → sélection automatique
          setActiveOrgId(userOrgs[0].org.id);
          setActiveRole(userOrgs[0].role);
        } else if (userOrgs.length > 1) {
          // Plusieurs orgs → garder la dernière sélectionnée (localStorage)
          const savedOrgId = localStorage.getItem(`tl_activeOrg_${user.uid}`);
          const found = userOrgs.find((o) => o.org.id === savedOrgId);
          if (found) {
            setActiveOrgId(found.org.id);
            setActiveRole(found.role);
          }
          // Sinon on laisse null → écran de sélection
        }
      } else {
        setBesoinAsso(false);
        setProfil(null);
        setOrgs([]);
        setActiveOrgId(null);
        setActiveRole(null);
      }
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // Changer d'organisation active
  function switchOrg(orgId) {
    const found = orgs.find((o) => o.org.id === orgId);
    if (!found) return;
    setActiveOrgId(orgId);
    setActiveRole(found.role);
    if (currentUser) {
      localStorage.setItem(`tl_activeOrg_${currentUser.uid}`, orgId);
    }
  }

  // Inscription avec création organisation
  async function inscrire({ email, password, nomOrganisation, pseudo, codePromo }) {
    inscriptionEnCours.current = true;
    let user;
    let orgId;
    try {
      ({ user } = await createUserWithEmailAndPassword(auth, email, password));

      // Créer le profil utilisateur (sans orgId)
      await creerProfil(user.uid, { email, pseudo });

      // Créer l'organisation (orgId = uid du créateur + timestamp pour unicité)
      orgId = `${user.uid}_${Date.now()}`;
      await creerOrganisation(orgId, {
        nom: nomOrganisation,
        plan: "starter",
        adminEmail: email,
        adminId: user.uid,
        codePromo: codePromo?.trim() || null,
      });
    } catch (e) {
      inscriptionEnCours.current = false;
      // Compte Auth créé mais profil/organisation en échec : on recharge,
      // la réparation automatique (onAuthStateChanged) termine l'inscription.
      if (user) { window.location.replace("/"); return user; }
      throw e;
    } finally {
      inscriptionEnCours.current = false;
    }

    const newOrg = { org: { id: orgId, nom: nomOrganisation, plan: "starter" }, role: "admin" };
    setOrgs([newOrg]);
    setActiveOrgId(orgId);
    setActiveRole("admin");
    setProfil({ email, pseudo: pseudo || email.split("@")[0] });

    // Envoyer l'email de confirmation d'inscription
    try {
      // Le serveur envoie à l'adresse du compte connecté, une seule fois
      await envoyerEmailInscription();
    } catch (e) {
      console.warn("Email inscription non envoyé:", e.message);
    }

    return user;
  }


  // Inscription sans création d'organisation (cas invitation)
  async function inscrireSansOrg({ email, password, pseudo }) {
    inscriptionEnCours.current = true;
    let user;
    try {
      ({ user } = await createUserWithEmailAndPassword(auth, email, password));
      await creerProfil(user.uid, { email, pseudo });
    } finally {
      inscriptionEnCours.current = false;
    }
    setProfil({ email, pseudo: pseudo || email.split("@")[0] });
    return user;
  }

  // Connexion / inscription avec Google (même bouton pour les deux)
  // Renvoie l'utilisateur, ou null si la page part vers Google (redirection).
  async function connecterGoogle() {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    // App installée (écran d'accueil) : les fenêtres popup y fonctionnent mal
    const installee = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
    if (installee) { await signInWithRedirect(auth, provider); return null; }
    try {
      const { user } = await signInWithPopup(auth, provider);
      return user;
    } catch (e) {
      if (e.code === "auth/popup-blocked") { await signInWithRedirect(auth, provider); return null; }
      throw e;
    }
  }

  // Termine la création du compte : prénom + nom de l'association
  async function finaliserCompte({ nomOrganisation, pseudo }) {
    if (!currentUser) return;
    const email = currentUser.email;
    const nom = nomOrganisation.trim().slice(0, 120);
    const prenom = (pseudo || "").trim().slice(0, 60);
    inscriptionEnCours.current = true;
    let orgId;
    try {
      const existant = await getProfil(currentUser.uid).catch(() => null);
      if (!existant) await creerProfil(currentUser.uid, { email, pseudo: prenom });
      else if (prenom && prenom !== existant.pseudo) await updatePseudo(currentUser.uid, prenom);
      orgId = `${currentUser.uid}_${Date.now()}`;
      await creerOrganisation(orgId, { nom, plan: "starter", adminEmail: email, adminId: currentUser.uid });
    } finally {
      inscriptionEnCours.current = false;
    }
    setOrgs([{ org: { id: orgId, nom, plan: "starter" }, role: "admin" }]);
    setActiveOrgId(orgId);
    setActiveRole("admin");
    setProfil((p) => ({ ...(p || {}), email, pseudo: prenom || email.split("@")[0], orgIds: [orgId] }));
    setBesoinAsso(false);
    try { await envoyerEmailInscription(); } catch (e) { console.warn("Email inscription non envoyé:", e.message); }
  }

  // Connexion
  async function connecter(email, password) {
    const { user } = await signInWithEmailAndPassword(auth, email, password);
    return user;
  }

  // Déconnexion
  // Efface aussi la copie locale des données (billets, acheteurs) stockée sur l'appareil :
  // indispensable quand un bénévole utilise un téléphone partagé ou prêté.
  async function deconnecter() {
    // 1. Ne jamais perdre une vente faite hors ligne et pas encore envoyée
    const synchro = await Promise.race([
      waitForPendingWrites(db).then(() => true),
      new Promise((r) => setTimeout(() => r(false), 5000)),
    ]);
    if (!synchro && !window.confirm(
      "⚠️ Des ventes ne sont pas encore synchronisées (pas de réseau).\n\n" +
      "Si vous vous déconnectez maintenant, elles seront PERDUES.\n" +
      "Cliquez sur Annuler, retrouvez du réseau, puis réessayez.\n\n" +
      "Se déconnecter quand même ?"
    )) return;

    await signOut(auth);
    setProfil(null); setOrgs([]); setActiveOrgId(null); setActiveRole(null);
    try {
      // On garde le préfixe et le compteur de l'appareil (numérotation des billets)
      Object.keys(localStorage)
        .filter((k) => k.startsWith("tl_activeOrg_") || k.startsWith("tl_activeTombola_"))
        .forEach((k) => localStorage.removeItem(k));
      sessionStorage.clear();
      await terminate(db);
      await clearIndexedDbPersistence(db);
    } catch (e) {
      console.warn("Nettoyage du cache local incomplet:", e?.message);
    }
    // Rechargement complet : l'instance Firestore terminée ne peut plus servir
    window.location.replace("/connexion");
  }

  // Réinitialisation mot de passe
  async function resetPassword(email) {
    // Email envoyé par le serveur avec le gabarit TomboLive (pas l'email Firebase par défaut)
    await envoyerLienMotDePasse(email);
  }

  // Changement mot de passe (depuis l'app)
  async function changerMotDePasse(ancienMdp, nouveauMdp) {
    const credential = EmailAuthProvider.credential(currentUser.email, ancienMdp);
    await reauthenticateWithCredential(currentUser, credential);
    await updatePassword(currentUser, nouveauMdp);
  }

  // Créer une nouvelle organisation pour un user existant
  async function creerNouvelleOrg(nomOrganisation) {
    if (!currentUser) return;
    const orgId = `${currentUser.uid}_${Date.now()}`;
    await creerOrganisation(orgId, {
      nom: nomOrganisation,
      plan: "starter",
      adminEmail: currentUser.email,
      adminId: currentUser.uid,
    });
    const newOrg = { org: { id: orgId, nom: nomOrganisation, plan: "starter" }, role: "admin" };
    setOrgs((prev) => [...prev, newOrg]);
    setActiveOrgId(orgId);
    setActiveRole("admin");
    localStorage.setItem(`tl_activeOrg_${currentUser.uid}`, orgId);
    return orgId;
  }

  const activeOrg = orgs.find((o) => o.org.id === activeOrgId)?.org || null;
  const isAdmin = activeRole === "admin";
  const isPro = activeOrg?.plan === "pro";
  const isStarter = activeOrg?.plan === "starter" || !activeOrg?.plan;
  const needsOrgSelection = !loading && currentUser && orgs.length > 1 && !activeOrgId;

  const value = {
    currentUser, profil, orgs, activeOrg, activeOrgId, activeRole,
    loading, switchOrg, inscrire, inscrireSansOrg, connecter, connecterGoogle, deconnecter,
    besoinAsso, finaliserCompte,
    resetPassword, changerMotDePasse, creerNouvelleOrg,
    orgId: activeOrgId,
    isAdmin, isPro, isStarter,
    needsOrgSelection,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
